import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import authService from '../api-authorization/AuthorizeService';
import StyledActionButton from "../GreenButton";
import { useEffect, useState } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Title from "../Title";
import RuleFilters from "./rulefilters";
import { Switch } from "@mui/material";
import Box from "@mui/material/Box";

/* helper notes + tooltips */
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PostingScheduleSettings } from "./projectPostingSchedule";

export default function FormDialog({ show = false, cb, existing }) {
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(Boolean((existing && existing.id) || show));

    const [projectName, setProjectName] = useState("");
    const [brands, setBrands] = useState({ brands: [] });
    const [existingProject, setExistingProject] = useState({ project_brands: [], project_channels: [] });
    const [channels, setChannels] = useState([]);

    const [keywords, setKeywords] = useState([]);

    const [require_link_to_brand, setrequire_link_to_brand] = useState(false);
    const [require_brand, setrequire_brand] = useState(false);
    const [require_audiable, setrequire_audiable] = useState(false);
    const [searchQueriesText, setSearchQueriesText] = useState("");

    // ✅ Mode helpers
    const hasExisting = Boolean(existing?.id);
    const isCreateMode = !hasExisting;
    const shouldShowCreateButton = !show && isCreateMode;

    const handleClickOpen = async () => setOpen(true);

    useEffect(() => {
        setOpen(Boolean((existing && existing.id) || show));
    }, [show, existing?.id]);

    const fetchChannels = async () => {
        const response = await fetch('/api/channels');
        const data = await response.json();
        setChannels(data);
    };

    const fetchBrands = async () => {
        const response = await fetch('/api/organisation/brands');
        const data = await response.json();
        setBrands(data);
    };

    const fetchData = async (id) => {
        setLoading(true);
        const response = await fetch('/api/projects/details/' + id);
        const data = await response.json();

        setExistingProject(data);
        authService.followIfLoginRedirect(response);

        setrequire_link_to_brand(Boolean(data.require_aff_link));
        setrequire_brand(Boolean(data.require_brand_mention));
        setrequire_audiable(Boolean(data.require_audiable_match));

        setProjectName(data.name || "");
        setKeywords(data.keywords);
        setSearchQueriesText((data.search_queries || []).join("\n"));

        setOpen(true);

        await Promise.all([fetchChannels(), fetchBrands()]);
        setLoading(false);
    };

    useEffect(() => {
        if (hasExisting) {
            fetchData(existing.id);
        } else {
            // create mode still needs these lists if you ever show them,
            // but keeping your existing behavior:
            fetchChannels();
            fetchBrands();
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existing?.id]);

    const handleClose = () => {
        setOpen(false);
        if (cb) cb();
    };

    const handleNameChange = (e) => setProjectName(e.target.value);

    /* one method to describe each toggle */
    const getToggleHelp = (key) => {
        switch (key) {
            case 'require_link_to_brand':
                return "Stricter rule. Video must include a clickable link to the brand’s site/profile. When this is ON, the Brand Mention rule is ignored.";
            case 'require_brand':
                return "The brand name must be mentioned in the title, description, tags, or spoken. Only applies when 'Require Link To Brand' is OFF.";
            case 'require_audiable':
                return "A project keyword or the brand must be spoken audibly in the video. On-screen text/captions alone don’t count.";
            default:
                return "";
        }
    };

    const handleChangeBrand = (id) => {
        let newBrands = existingProject.project_brands || [];
        if (newBrands.find((e) => e.id === id)) {
            newBrands = newBrands.filter((e) => e.id !== id);
        } else {
            newBrands = [...newBrands, { id }];
        }
        setExistingProject({ ...existingProject, project_brands: newBrands });
    };

    const handleChangeChannel = (id) => {
        let newChannels = existingProject.project_channels || [];
        if (newChannels.find((e) => e.id === id)) {
            newChannels = newChannels.filter((e) => e.id !== id);
        } else {
            newChannels = [...newChannels, { id }];
        }
        setExistingProject({ ...existingProject, project_channels: newChannels });
    };

    useEffect(() => {
        setOpen(show);
    }, [show]);

    const create = async () => {
        // ✅ Create mode: ONLY send the name
        const baseReq = { name: projectName };

        // ✅ Edit mode: send the full payload (your existing behavior)
        let req = baseReq;

        if (hasExisting) {
            const requirePostingSchedule = Boolean(existingProject?.require_posting_schedule);

            const postingScheduleStartDate = requirePostingSchedule
                ? (existingProject?.posting_schedule_start_date ?? null)
                : null;

            const rawConfig = existingProject?.posting_schedule_config || {};
            const scheduleType = Number.isFinite(rawConfig.schedule_type) ? rawConfig.schedule_type : 0;
            let threshold = Number.isFinite(rawConfig.threshold) ? rawConfig.threshold : 0;

            if (scheduleType === 1 || scheduleType === 2) {
                threshold = Math.max(1, Math.floor(threshold || 1));
            } else {
                threshold = 0;
            }

            const postingScheduleConfig = requirePostingSchedule
                ? { schedule_type: scheduleType, threshold }
                : {};

            req = {
                ...baseReq,
                keywords: JSON.stringify(keywords),
                project_brands: existingProject.project_brands ? existingProject.project_brands.map((e) => e.id) : [],
                project_channels: existingProject.project_channels ? existingProject.project_channels.map((e) => e.id) : [],
                require_aff_link: require_link_to_brand,
                require_brand_mention: require_brand,
                require_audiable_match: require_audiable,
                require_posting_schedule: requirePostingSchedule,
                posting_schedule_config: postingScheduleConfig,
                posting_schedule_start_date: postingScheduleStartDate,
                search_queries: searchQueriesText
                    .split("\n")
                    .map((line) => line.trim())
                    .filter((line, index, values) => line && values.indexOf(line) === index),
            };
        }

        const response = await fetch(hasExisting ? `/api/projects/${existing.id}` : "/api/projects", {
            method: hasExisting ? "PUT" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(req),
        });

        authService.followIfLoginRedirect(response);
        await response.json();

        setOpen(false);
        setProjectName("");
        if (cb) cb();
    };

    return (
        <div>
            {shouldShowCreateButton && (
                <StyledActionButton onClick={handleClickOpen}>Create</StyledActionButton>
            )}

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>{hasExisting ? 'Edit' : 'Create'} Project</DialogTitle>

                <DialogContent>
                    {/* ✅ ALWAYS visible */}
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Project Name"
                        type="text"
                        fullWidth
                        variant="standard"
                        value={projectName}
                        onChange={handleNameChange}
                    />

                    {/* ✅ Everything else hidden when creating */}
                    {!isCreateMode && (
                        <>
                            {/* Matching rules */}
                            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="overline" color="text.secondary">Matching Rules</Typography>

                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel
                                            sx={{ ml: 0 }}
                                            control={
                                                <Switch
                                                    checked={require_link_to_brand}
                                                    onChange={(e) => setrequire_link_to_brand(e.target.checked)}
                                                />
                                            }
                                            label="Require Link To Brand"
                                        />
                                        <Tooltip title={getToggleHelp('require_link_to_brand')} arrow>
                                            <IconButton size="small" aria-label="Require Link To Brand info">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                            </Box>

                            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="overline" color="text.secondary">Project Search</Typography>
                                <TextField
                                    margin="dense"
                                    label="Search Queries"
                                    type="text"
                                    fullWidth
                                    variant="standard"
                                    multiline
                                    minRows={4}
                                    value={searchQueriesText}
                                    onChange={(e) => setSearchQueriesText(e.target.value)}
                                    helperText="One search per line. Leave blank to disable project search. Enabled searches are picked up automatically by the background search task."
                                />
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                                Tip: <strong>Require Link To Brand</strong> is stricter and overrides <strong>Require Brand Mention</strong>.
                            </Typography>

                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel
                                            sx={{ ml: 0 }}
                                            control={
                                                <Switch
                                                    checked={require_brand}
                                                    onChange={(e) => setrequire_brand(e.target.checked)}
                                                />
                                            }
                                            label="Require Brand Mention"
                                        />
                                        <Tooltip title={getToggleHelp('require_brand')} arrow>
                                            <IconButton size="small" aria-label="Require Brand Mention info">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <FormControlLabel
                                            sx={{ ml: 0 }}
                                            control={
                                                <Switch
                                                    checked={require_audiable}
                                                    onChange={(e) => setrequire_audiable(e.target.checked)}
                                                />
                                            }
                                            label="Require Audible Match"
                                        />
                                        <Tooltip title={getToggleHelp('require_audiable')} arrow>
                                            <IconButton size="small" aria-label="Require Audible Match info">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Posting schedule */}
                            <Title>Require Posting Schedule</Title>
                            <DialogContentText>
                                If your selected channels are required to post about your brand at a schedule, you can set that here
                            </DialogContentText>

                            {!loading && (
                                <PostingScheduleSettings
                                    existingProject={existingProject}
                                    setExistingProject={setExistingProject}
                                />
                            )}

                            {/* Rule filters / keywords */}
                            <DialogContentText sx={{ mt: 3 }}>
                                Enter keywords for the project. These keywords will be words that videos will be searched for.
                            </DialogContentText>

                            {hasExisting && !loading && (
                                <RuleFilters
                                    projectId={existing.id}
                                    initialFilterRulesJson={keywords}
                                    onSaved={(data) => {
                                        setKeywords(data);
                                    }}
                                />
                            )}

                            {/* Brands */}
                            <Title>Brands</Title>
                            <DialogContentText>
                                Here you can select a brand to associate with the project. This will allow us to report visual mentions of brands in the media even if a label doesn't qualify.
                            </DialogContentText>

                            {brands.brands.length > 0 &&
                                brands.brands.map((e) => (
                                    <FormControlLabel
                                        key={e.id}
                                        control={
                                            <Checkbox
                                                key={'cb' + e.id}
                                                checked={Boolean(
                                                    existingProject?.project_brands?.find((f) => f.id === e.id)
                                                )}
                                                name={e.name}
                                                onChange={() => handleChangeBrand(e.id)}
                                            />
                                        }
                                        label={e.name}
                                    />
                                ))}

                            <Divider sx={{ my: 3 }} />

                            {/* Channels */}
                            <Title>Channels</Title>
                            <DialogContentText>
                                Limit project to certain channels. Select channels below. Only channels identified below will be reported to this project.
                            </DialogContentText>

                            {channels.length > 0 &&
                                channels.map((e) => (
                                    <FormControlLabel
                                        key={e.channel.id}
                                        control={
                                            <Checkbox
                                                key={'cbchan' + e.channel.id}
                                                checked={Boolean(
                                                    existingProject?.project_channels?.find((f) => f.id === e.channel.id)
                                                )}
                                                name={e.channel.handle}
                                                onChange={() => handleChangeChannel(e.channel.id)}
                                            />
                                        }
                                        label={e.channel.handle}
                                    />
                                ))}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={create}>{isCreateMode ? 'Create' : 'Save'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
